import React from 'react'
import type { WidgetTypeCode } from '../../constants/widgetTypes'

interface WidgetTypeIconProps {
  code: string
  size?: number
  className?: string
}

const commonProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const VideoEmbedIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
    <polygon points="10 9 16 12 10 15 10 9" fill="currentColor" stroke="none" />
  </svg>
)

const EditorialIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <line x1="21" y1="6" x2="3" y2="6" />
    <line x1="21" y1="12" x2="3" y2="12" />
    <line x1="17" y1="18" x2="3" y2="18" />
  </svg>
)

const BannerImageIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
)

const GoogleAdSlotIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
)

const CarouselIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <rect x="6" y="6" width="12" height="12" rx="1" ry="1" />
    <path d="M3 8v8" />
    <path d="M21 8v8" />
  </svg>
)

// A full-width banner frame (sun + mountain read as "image") over rotation dots
// that signal the rotating slides — distinguishing it from banner_image/carousel.
const HeroBannerIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <rect x="3" y="4" width="18" height="12" rx="2" ry="2" />
    <circle cx="8" cy="20" r="0.9" />
    <circle cx="12" cy="20" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="16" cy="20" r="0.9" />
  </svg>
)

// A skinny full-width strip with a leading alert dot — reads as "one-line
// notice", distinguishing it from the taller banner_image/hero_banner frames.
const NotificationBannerIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <rect x="2" y="9" width="20" height="6" rx="2" ry="2" />
    <circle cx="6" cy="12" r="1" fill="currentColor" stroke="none" />
    <line x1="9" y1="12" x2="19" y2="12" />
  </svg>
)

// A speech bubble with a question mark — reads as "questions and answers",
// distinguishing it from editorial's plain text lines.
const FaqIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10z" />
    <path d="M9.6 9a2.4 2.4 0 1 1 3.4 2.2c-.7.3-1 .8-1 1.4" />
    <circle cx="12" cy="14.8" r="0.5" fill="currentColor" stroke="none" />
  </svg>
)

// A trophy cup — reads as "tournament", distinguishing the tournament rail
// from the content carousels' card frames.
const TournamentIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <path d="M8 3h8v6a4 4 0 0 1-8 0V3z" />
    <path d="M8 5H5a3 3 0 0 0 3 4" />
    <path d="M16 5h3a3 3 0 0 1-3 4" />
    <line x1="12" y1="13" x2="12" y2="17" />
    <path d="M8 21h8" />
    <path d="M10 17h4v4" />
  </svg>
)

// A 2x2 block of cards — reads as "wrapping grid", distinguishing it from the
// carousel's single frame with peeking neighbors.
const GridIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <rect x="3" y="3" width="8" height="8" rx="1" ry="1" />
    <rect x="13" y="3" width="8" height="8" rx="1" ry="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" ry="1" />
    <rect x="13" y="13" width="8" height="8" rx="1" ry="1" />
  </svg>
)

// A card with a price in it — reads as "what a plan costs". The currency mark
// is the whole point: it is the only money iconography in the set, and it is
// what separates this frame from video_embed, banner_image and hero_banner,
// which are all a rounded rectangle too.
const PlanSelectionIcon: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <svg {...commonProps} width={size} height={size} className={className} aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="15" rx="2" ry="2" />
    <path d="M14 10c0-1.1-.9-1.7-2-1.7s-2 .6-2 1.7.9 1.4 2 1.7 2 .6 2 1.7-.9 1.7-2 1.7-2-.6-2-1.7" />
    <path d="M12 7.2v9.6" />
  </svg>
)

// Every widget type maps to an icon here. Typed as a complete record of
// WidgetTypeCode so adding a type to WIDGET_TYPES without an icon fails to
// compile — the previous switch fell through to null, which showed the type in
// the picker with an empty space where its icon belongs and warned no one.
const ICONS: Record<WidgetTypeCode, React.FC<{ size: number; className?: string }>> = {
  video_embed: VideoEmbedIcon,
  editorial: EditorialIcon,
  banner_image: BannerImageIcon,
  google_ad_slot: GoogleAdSlotIcon,
  standard_landscape_carousel: CarouselIcon,
  standard_portrait_carousel: CarouselIcon,
  small_landscape_carousel: CarouselIcon,
  large_landscape_carousel: CarouselIcon,
  hero_banner: HeroBannerIcon,
  notification_banner: NotificationBannerIcon,
  faq: FaqIcon,
  standard_landscape_grid: GridIcon,
  plan_selection: PlanSelectionIcon,
  tournament_carousel: TournamentIcon,
}

const WidgetTypeIcon: React.FC<WidgetTypeIconProps> = ({ code, size = 16, className }) => {
  // `code` is a plain string at the call sites (it comes from stored widget
  // rows), so an unrecognised value still has to resolve to nothing.
  const Icon = ICONS[code as WidgetTypeCode]
  return Icon ? <Icon size={size} className={className} /> : null
}

export default WidgetTypeIcon
